import React, { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import {
  Text,
  Banner,
  KeyboardScreenContainer,
  Input,
  Button,
} from '~/components';
import * as yup from 'yup';
import { isCpf } from '~/utils/yup';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '~/navigation/types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useAppNavigation from '~/hooks/useAppNavigation';
import { useCheckout, useSalesFunnel } from '~/stores';
import NoDataFoundModal from './components/NoDataFoundModal';
import { getStoreInformationUseCase } from '~/useCases/PSF/GetStoreInformationUseCase';
import { getSellerInformationUseCase } from '~/useCases/PSF/GetSellerInformationUseCase';
import { LocalStorage } from '~/storage/LocalStorage';
import { CONTRACT_STORE_CODE } from '~/storage/constants';
import { ContractSellerStepNames } from '~/utils/salesFunnel';

interface FormData {
  document: string;
  store: string;
}

export const schema = yup
  .object()
  .shape({
    document: isCpf(),
    store: yup.string().required('Este campo é obrigatório'),
  })
  .required();

const ContractSellerInfo: React.FC = () => {
  const { navigate } = useAppNavigation();
  const { setContractSeller } = useCheckout();
  const { newStepLog } = useSalesFunnel();

  const [noDataModalVisible, setNoDataModalVisible] = useState(false);
  const [noDataModalMessage, setNoDataModalMessage] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(false);

  const { params } =
    useRoute<
      RouteProp<RootStackParamList, 'MarisaServicos_ContractSellerInfo'>
    >();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onContinue = useCallback(() => {
    navigate('MarisaServicos_ContractSellerConfirmation', {
      type: params.type,
    });
  }, [navigate, params.type]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      setLoading(true);
      const [sellerFoundResponse, storeFoundResponse] = await Promise.all([
        getSellerInformationUseCase(data.document),
        getStoreInformationUseCase(data.store),
        LocalStorage.set(CONTRACT_STORE_CODE, data.store),
      ]);
      setLoading(false);

      let modalMessage;

      if (!sellerFoundResponse && !storeFoundResponse) {
        modalMessage = 'Dados do vendedor e da loja não foram localizados';
      } else if (!sellerFoundResponse) {
        modalMessage = 'CPF do(a) vendedor(a) não encontrado';
      } else if (!storeFoundResponse) {
        modalMessage = 'Dados da loja não foram localizados';
      }

      setContractSeller({
        seller: {
          document: data.document,
          name: sellerFoundResponse ? sellerFoundResponse.name : undefined,
        },
        store: {
          code: data.store,
          address: storeFoundResponse ? storeFoundResponse.address : undefined,
          name: storeFoundResponse ? storeFoundResponse.name : undefined,
        },
      });

      if (modalMessage) {
        setNoDataModalMessage(modalMessage);
        setNoDataModalVisible(true);
      } else {
        onContinue();
      }
    },
    [onContinue, setContractSeller]
  );

  useEffect(() => {
    async function loadStoreCode() {
      const storeCode = await LocalStorage.get(CONTRACT_STORE_CODE);

      if (storeCode) {
        setValue('store', storeCode);
      }
    }

    loadStoreCode();
  }, [setValue]);

  useEffect(() => {
    newStepLog(ContractSellerStepNames.DOCUMENT);
  }, [newStepLog]);

  return (
    <View className="flex-1 bg-light-100">
      <KeyboardScreenContainer>
        <View className="flex-1 container mx-auto xl:flex-row xl:pt-12 xl:pb-24">
          <Banner type={params.type} />
          <View className="mt-8 xl:mt-0 px-6 flex-1 xl:ml-24">
            <Text className="text-xl mb-4 text-gray-600">Dados da Loja</Text>

            <Text className="text-gray-400 text-sm mb-8">
              Para prosseguir, é necessário o dado do vendedor e a identificação
              da loja
            </Text>

            <Input
              control={control}
              label="Informe o seu CPF"
              name="document"
              placeholder="Informe o seu CPF"
              keyboardType="number-pad"
              error={errors.document?.message}
              maskConfig={{ type: 'cpf' }}
            />

            <Input
              control={control}
              label="Loja"
              name="store"
              placeholder="Informe a loja"
              error={errors.store?.message}
            />

            <View className="flex-1 justify-end">
              <Button
                className="w-full"
                loading={loading}
                onPress={handleSubmit(onSubmit)}
              >
                CONTINUAR
              </Button>
            </View>
          </View>
        </View>
      </KeyboardScreenContainer>

      <NoDataFoundModal
        visible={noDataModalVisible}
        message={noDataModalMessage ?? ''}
        onContinue={onContinue}
        onClose={() => setNoDataModalVisible(false)}
      />
    </View>
  );
};
export default ContractSellerInfo;
